import { api } from './api.js';

/**
 * The invoice and the Test Certificate of every batch on it, as one document
 * to print or save as PDF. The server has no PDF engine to merge them, and a
 * browser cannot print several PDFs in one job, so each certificate page is
 * drawn here (pdf.js) and placed after the invoice as a page of its own.
 *
 * `win` is opened by the caller in the click itself, or a pop-up blocker
 * stops it once the downloads have taken their time.
 */
export async function openPrintSet({ invoiceId, certificates, win, onProgress }) {
  const target = win || window.open('', '_blank');
  if (!target) throw new Error('The browser blocked the new window; allow pop-ups for InvIT and try again.');
  try {
    const say = (text) => {
      if (onProgress) onProgress(text);
      try { target.document.body.textContent = text; } catch { /* window not ready */ }
    };
    say('Preparing the invoice…');
    const invoiceHtml = await (await api.raw(`/api/invoices/${invoiceId}/print`)).text();

    const pdfjs = await import('pdfjs-dist');
    const worker = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
    pdfjs.GlobalWorkerOptions.workerSrc = worker;

    const pages = [];
    for (const [n, tc] of certificates.entries()) {
      say(`Adding test certificate ${n + 1} of ${certificates.length} (${tc.batch_no})…`);
      const data = await (await api.raw(`/api/test-certificates/${tc.tc_id}/file`)).arrayBuffer();
      const doc = await pdfjs.getDocument({ data }).promise;
      for (let p = 1; p <= doc.numPages; p += 1) {
        const page = await doc.getPage(p);
        // About 200 dpi on A4: sharp on paper without a page weighing megabytes.
        const viewport = page.getViewport({ scale: 2.2 });
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport }).promise;
        pages.push({ batch: tc.batch_no, src: canvas.toDataURL('image/jpeg', 0.9), landscape: viewport.width > viewport.height });
        page.cleanup();
      }
      await doc.destroy();
    }

    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const extra = `
<style>
  .tc-page { break-before: page; page-break-before: always; height: 277mm; display: flex; align-items: center; justify-content: center; }
  .tc-page img { max-width: 100%; max-height: 100%; display: block; }
  @media screen { .tc-page { height: auto; margin: 16px 0; border-top: 1px dashed #bbb; padding-top: 16px; } }
</style>
${pages.map((p) => `<div class="tc-page"><img alt="Test certificate, batch ${esc(p.batch)}" src="${p.src}"></div>`).join('\n')}
<script>
  window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 300); });
</script>`;
    const html = invoiceHtml.includes('</body>')
      ? invoiceHtml.replace(/<\/body>(?![\s\S]*<\/body>)/, `${extra}</body>`)
      : invoiceHtml + extra;
    target.document.open();
    target.document.write(html);
    target.document.close();
    return { pages: pages.length };
  } catch (e) {
    try { target.close(); } catch { /* already gone */ }
    throw e;
  }
}
